import { useEffect, useState } from "react";

import type { Entry } from "../api/types";

interface Props {
  /** Present when editing; absent when adding. */
  entry?: Entry;
  disabled?: boolean;
  onSubmit: (values: { task: string; hours: number; minutes: number; note: string }) => Promise<void>;
  onCancel?: () => void;
}

/**
 * Hours and minutes are deliberately two separate fields, never a decimal number of hours —
 * that is called out explicitly in the spec.
 */
export function EntryForm({ entry, disabled, onSubmit, onCancel }: Props): JSX.Element {
  const [task, setTask] = useState(entry?.task ?? "");
  const [hours, setHours] = useState(String(entry?.hours_part ?? 0));
  const [minutes, setMinutes] = useState(String(entry?.minutes_part ?? 0));
  const [note, setNote] = useState(entry?.note ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setTask(entry?.task ?? "");
    setHours(String(entry?.hours_part ?? 0));
    setMinutes(String(entry?.minutes_part ?? 0));
    setNote(entry?.note ?? "");
  }, [entry]);

  const total = Number(hours || 0) * 60 + Number(minutes || 0);

  async function handleSubmit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setError(null);
    if (!task.trim()) {
      setError("Give the entry a task name.");
      return;
    }
    if (total <= 0) {
      setError("Log more than zero minutes.");
      return;
    }
    setBusy(true);
    try {
      await onSubmit({
        task: task.trim(),
        hours: Number(hours || 0),
        minutes: Number(minutes || 0),
        note: note.trim(),
      });
      if (!entry) {
        setTask("");
        setHours("0");
        setMinutes("0");
        setNote("");
      }
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : "Could not save.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="entry-form" onSubmit={(event) => void handleSubmit(event)}>
      <label className="field">
        <span>Task</span>
        <input
          type="text"
          value={task}
          disabled={disabled || busy}
          maxLength={500}
          placeholder="What did you work on?"
          onChange={(event) => setTask(event.target.value)}
        />
      </label>

      <div className="time-fields">
        <label className="field small">
          <span>Hours</span>
          <input
            type="number"
            min={0}
            max={24}
            value={hours}
            disabled={disabled || busy}
            onChange={(event) => setHours(event.target.value)}
          />
        </label>
        <label className="field small">
          <span>Minutes</span>
          <input
            type="number"
            min={0}
            max={59}
            value={minutes}
            disabled={disabled || busy}
            onChange={(event) => setMinutes(event.target.value)}
          />
        </label>
      </div>

      <label className="field">
        <span>Note (optional)</span>
        <textarea
          value={note}
          rows={2}
          maxLength={4000}
          disabled={disabled || busy}
          onChange={(event) => setNote(event.target.value)}
        />
      </label>

      {error && <p className="error">{error}</p>}

      <div className="form-actions">
        <button type="submit" className="primary" disabled={disabled || busy}>
          {entry ? "Save changes" : "Add entry"}
        </button>
        {onCancel && (
          <button type="button" className="ghost" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}
